import React, { useEffect, useState, useCallback } from "react";
import {
  FaEdit,
  FaTrash,
  FaBold,
  FaItalic,
  FaUnderline,
  FaListUl,
  FaListOl,
  FaQuoteRight,
  FaImage,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaLink,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import { BlogAPI } from "../../api/api";
import "bootstrap/dist/css/bootstrap.min.css";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import ImageExtension from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";

const resizeImage = (file, maxSizeMB = 5, maxWidth = 1920, maxHeight = 1080) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new window.Image();
      img.src = event.target.result;
      img.onload = () => {
        let canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        const scale = Math.min(maxWidth / width, maxHeight / height, 1);
        width = width * scale;
        height = height * scale;
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        const compress = (quality = 0.9) => {
          canvas.toBlob(
            (blob) => {
              if (blob.size / 1024 / 1024 > maxSizeMB && quality > 0.1) {
                compress(quality - 0.1);
              } else {
                const reader2 = new FileReader();
                reader2.readAsDataURL(blob);
                reader2.onload = () => resolve(reader2.result);
                reader2.onerror = reject;
              }
            },
            "image/jpeg",
            quality
          );
        };

        compress();
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });

const countWords = (html) => {
  if (!html) return 0;
  const text = html.replace(/<[^>]*>/g, "").trim();
  if (!text) return 0;
  return text.split(/\s+/).length;
};

const calcReadingTime = (wordCount) => Math.max(1, Math.ceil(wordCount / 200));

export default function BlogManagement() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [editingPostId, setEditingPostId] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("General");
  const [slug, setSlug] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [content, setContent] = useState("");
  const [metaDescripcion, setMetaDescripcion] = useState("");
  const [extracto, setExtracto] = useState("");
  const [showSeoPreview, setShowSeoPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const categories = ["General", "Psicología", "Bienestar", "Terapia"];

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      ImageExtension,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({ openOnClick: false }),
    ],
    content: "",
    onUpdate: ({ editor }) => setContent(editor.getHTML()),
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await BlogAPI.getAll();
      setPosts(data);
    } catch {
      setError("Error al obtener los posts");
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setError("Formato de imagen no permitido. Solo jpg, png o webp.");
      return false;
    }
    return true;
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !validateFile(file)) return;
    try {
      const base64 = await resizeImage(file, 5);
      setImageFile(base64);
      setImagePreview(base64);
    } catch {
      setError("Error al procesar la imagen");
    }
  };

  const addImageToEditor = async (e) => {
    const file = e.target.files[0];
    if (!file || !editor || !validateFile(file)) return;
    try {
      const base64 = await resizeImage(file, 2);
      editor.chain().focus().setImage({ src: base64 }).run();
    } catch {
      setError("Error al insertar imagen en el contenido");
    }
  };

  const addLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL del enlace:", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const resetForm = () => {
    setEditingPostId(null);
    setTitle("");
    setCategory("General");
    setSlug("");
    setImageFile(null);
    setImagePreview(null);
    setContent("");
    setMetaDescripcion("");
    setExtracto("");
    setShowSeoPreview(false);
    setShowPreview(false);
    if (editor) editor.commands.setContent("");
  };

  const handleSlugChange = (text) => {
    setSlug(text.toLowerCase().replace(/\s+/g, "-"));
  };

  const handleSubmit = async () => {
    if (!title || !content || !category) {
      setError("Título, contenido y categoría son obligatorios");
      return;
    }

    const blogData = {
      titulo: title,
      contenido: content,
      categoria: category,
      slug,
      imagenPortada: imageFile ? imageFile.split(",")[1] : null,
      metaDescripcion: metaDescripcion || null,
      extracto: extracto || null,
      fechaActualizacion: new Date().toISOString(),
    };

    try {
      if (editingPostId) {
        await BlogAPI.update(editingPostId, blogData);
      } else {
        await BlogAPI.create(blogData);
      }
      resetForm();
      fetchPosts();
    } catch {
      setError("Error al guardar el artículo");
    }
  };

  const handleEdit = (post) => {
    setEditingPostId(post.id);
    setTitle(post.titulo);
    setCategory(post.categoria || "General");
    setSlug(post.slug);
    setMetaDescripcion(post.metaDescripcion || "");
    setExtracto(post.extracto || "");

    if (editor) {
      editor.commands.setContent(post.contenido);
    }

    if (post.imagenPortada) {
      const base64 = `data:image/jpeg;base64,${post.imagenPortada}`;
      setImagePreview(base64);
      setImageFile(base64);
    } else {
      setImagePreview(null);
      setImageFile(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Seguro que quieres eliminar este post?")) return;
    try {
      await BlogAPI.delete(id);
      fetchPosts();
    } catch {
      setError("Error al eliminar el post");
    }
  };

  const wordCount = countWords(content);
  const readingTime = calcReadingTime(wordCount);

  const ToolbarBtn = ({ onClick, active, children, title }) => (
    <button
      className={`btn ${active ? "btn-success text-white" : "btn-light"}`}
      onClick={onClick}
      title={title}
      type="button"
    >
      {children}
    </button>
  );

  return (
    <div className="container my-5">
      <h2 className="mb-4 text-center text-success">Gestión del Blog</h2>

      <div className="card shadow-sm p-4 mb-5">
        <h4>{editingPostId ? "Editar Artículo" : "Nuevo Artículo"}</h4>

        {error && <div className="alert alert-danger">{error}</div>}

        <input
          type="text"
          className="form-control mb-2"
          placeholder="Título"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            handleSlugChange(e.target.value);
          }}
        />

        <input
          type="text"
          className="form-control mb-2"
          placeholder="Slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
        />

        <select
          className="form-select mb-3"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          {categories.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>

        {/* Toolbar */}
        {editor && (
          <div className="border rounded p-2 mb-2 bg-light">
            <div className="d-flex gap-2 flex-wrap">
              <ToolbarBtn
                onClick={() => editor.chain().focus().toggleBold().run()}
                active={editor.isActive("bold")}
                title="Negrita"
              >
                <FaBold />
              </ToolbarBtn>
              <ToolbarBtn
                onClick={() => editor.chain().focus().toggleItalic().run()}
                active={editor.isActive("italic")}
                title="Cursiva"
              >
                <FaItalic />
              </ToolbarBtn>
              <ToolbarBtn
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                active={editor.isActive("underline")}
                title="Subrayado"
              >
                <FaUnderline />
              </ToolbarBtn>

              <div className="vr" />

              <ToolbarBtn
                onClick={() =>
                  editor.chain().focus().toggleHeading({ level: 2 }).run()
                }
                active={editor.isActive("heading", { level: 2 })}
                title="Título H2"
              >
                H2
              </ToolbarBtn>
              <ToolbarBtn
                onClick={() =>
                  editor.chain().focus().toggleHeading({ level: 3 }).run()
                }
                active={editor.isActive("heading", { level: 3 })}
                title="Título H3"
              >
                H3
              </ToolbarBtn>

              <div className="vr" />

              <ToolbarBtn
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                active={editor.isActive("bulletList")}
                title="Lista"
              >
                <FaListUl />
              </ToolbarBtn>
              <ToolbarBtn
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                active={editor.isActive("orderedList")}
                title="Lista numerada"
              >
                <FaListOl />
              </ToolbarBtn>
              <ToolbarBtn
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                active={editor.isActive("blockquote")}
                title="Cita"
              >
                <FaQuoteRight />
              </ToolbarBtn>

              <div className="vr" />

              <ToolbarBtn
                onClick={() =>
                  editor.chain().focus().setTextAlign("left").run()
                }
                active={editor.isActive({ textAlign: "left" })}
                title="Alinear izquierda"
              >
                <FaAlignLeft />
              </ToolbarBtn>
              <ToolbarBtn
                onClick={() =>
                  editor.chain().focus().setTextAlign("center").run()
                }
                active={editor.isActive({ textAlign: "center" })}
                title="Centrar"
              >
                <FaAlignCenter />
              </ToolbarBtn>
              <ToolbarBtn
                onClick={() =>
                  editor.chain().focus().setTextAlign("right").run()
                }
                active={editor.isActive({ textAlign: "right" })}
                title="Alinear derecha"
              >
                <FaAlignRight />
              </ToolbarBtn>

              <div className="vr" />

              <ToolbarBtn
                onClick={addLink}
                active={editor.isActive("link")}
                title="Enlace"
              >
                <FaLink />
              </ToolbarBtn>

              <label className="btn btn-light m-0" title="Insertar imagen">
                <FaImage />
                <input type="file" hidden onChange={addImageToEditor} />
              </label>

              <div className="vr" />

              <ToolbarBtn
                onClick={() => setShowPreview(!showPreview)}
                active={showPreview}
                title="Vista previa"
              >
                {showPreview ? <FaEyeSlash /> : <FaEye />}
              </ToolbarBtn>
            </div>
          </div>
        )}

        {/* Editor / Preview */}
        {showPreview ? (
          <div
            className="mb-3 border rounded p-4 bg-white"
            style={{ minHeight: 200 }}
          >
            <h2 className="mb-3">{title || "Sin título"}</h2>
            <div dangerouslySetInnerHTML={{ __html: content }} />
          </div>
        ) : (
          <div className="mb-3 border p-2" style={{ minHeight: 200 }}>
            <EditorContent editor={editor} />
          </div>
        )}

        {/* Word count footer */}
        <div className="d-flex justify-content-end gap-3 mb-3 text-muted small">
          <span>{wordCount} palabras</span>
          <span>~{readingTime} min de lectura</span>
        </div>

        {/* SEO Fields */}
        <div className="card bg-light p-3 mb-3">
          <h6 className="mb-3">Campos SEO</h6>

          <div className="mb-2">
            <label className="form-label small mb-1">
              Meta descripción ({metaDescripcion.length}/160)
            </label>
            <textarea
              className="form-control"
              rows={2}
              maxLength={160}
              placeholder="Descripción para motores de búsqueda (max 160 caracteres)"
              value={metaDescripcion}
              onChange={(e) => setMetaDescripcion(e.target.value)}
            />
          </div>

          <div className="mb-2">
            <label className="form-label small mb-1">
              Extracto ({extracto.length}/300)
            </label>
            <textarea
              className="form-control"
              rows={3}
              maxLength={300}
              placeholder="Resumen del artículo para listados (max 300 caracteres)"
              value={extracto}
              onChange={(e) => setExtracto(e.target.value)}
            />
          </div>

          {/* Google Preview */}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary mb-2"
            onClick={() => setShowSeoPreview(!showSeoPreview)}
          >
            {showSeoPreview ? "Ocultar" : "Ver"} vista previa en Google
          </button>

          {showSeoPreview && (
            <div className="p-3 bg-white border rounded" style={{ fontFamily: "Arial, sans-serif" }}>
              <div style={{ color: "#1a0dab", fontSize: 18, marginBottom: 2 }}>
                {title || "Título del artículo"} | Inerva Psicología
              </div>
              <div style={{ color: "#006621", fontSize: 13, marginBottom: 2 }}>
                www.inervapsicologia.es/blog/{slug || "slug-del-articulo"}
              </div>
              <div style={{ color: "#545454", fontSize: 13 }}>
                {metaDescripcion || "Añade una meta descripción para ver cómo aparecerá en Google..."}
              </div>
            </div>
          )}
        </div>

        {/* Cover image */}
        <input
          type="file"
          className="form-control mb-2"
          onChange={handleImageChange}
        />
        {imagePreview && (
          <img
            src={imagePreview}
            className="img-fluid mb-2 rounded"
            style={{ maxHeight: 200, objectFit: "cover" }}
          />
        )}

        <div className="d-flex gap-2">
          <button
            className={`btn ${editingPostId ? "btn-warning" : "btn-success"} flex-fill`}
            onClick={handleSubmit}
          >
            {editingPostId ? "Actualizar Artículo" : "Añadir Artículo"}
          </button>
          {editingPostId && (
            <button className="btn btn-secondary flex-fill" onClick={resetForm}>
              Cancelar
            </button>
          )}
        </div>
      </div>

      <div className="row g-4">
        {posts.map((post) => (
          <div key={post.id} className="col-md-6 col-lg-4">
            <div className="card h-100 shadow-sm">
              {post.imagenPortada && (
                <img
                  src={`data:image/jpeg;base64,${post.imagenPortada}`}
                  className="card-img-top"
                  style={{ objectFit: "cover", height: 200 }}
                />
              )}
              <div className="card-body d-flex flex-column">
                <h5>{post.titulo}</h5>
                <p
                  className="flex-grow-1 text-truncate"
                  style={{ maxHeight: 60, overflow: "hidden" }}
                >
                  {post.extracto || post.contenido?.replace(/<[^>]*>/g, "").substring(0, 100)}
                </p>
                <small className="text-muted">
                  {post.categoria} | {post.tiempoLectura || "?"} min |{" "}
                  {new Date(post.fechaPublicacion).toLocaleDateString()}
                </small>
                <div className="d-flex justify-content-between mt-3">
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={() => handleEdit(post)}
                  >
                    <FaEdit /> Editar
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(post.id)}
                  >
                    <FaTrash /> Eliminar
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
